import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Building2,
  CalendarDays,
  DollarSign,
  Users,
  Clock,
  ArrowRight,
  CheckCircle2,
  Star,
  Gift,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import { useT, useLang } from '@/i18n';

const DashboardPage = () => {
  const t = useT();
  const [lang] = useLang();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customerStats, setCustomerStats] = useState({
    totalBookings: 0,
    upcomingBookings: 0,
    totalSpent: 0,
    loyaltyPoints: 0,
  });

  // Pass role explicitly to avoid stale closure
  const fetchData = async (role) => {
    try {
      if (role === 'CUSTOMER') {
        const bookingsRes = await axios.get(`${API}/bookings`);
        const all = bookingsRes.data.items || [];
        const today = new Date().toISOString().split('T')[0];
        const upcoming = all.filter(
          (b) => b.start_date >= today && b.status !== 'CANCELLED'
        );
        const totalSpent = all
          .filter((b) => b.status !== 'CANCELLED')
          .reduce((sum, b) => {
            const pb = b.price_breakdown || {};
            return sum + (pb.total_price ?? pb.grand_total ?? pb.total ?? 0);
          }, 0);
        setCustomerStats({
          totalBookings: all.length,
          upcomingBookings: upcoming.length,
          totalSpent,
          loyaltyPoints: Math.floor(totalSpent / 2),
        });
        setRecentBookings(all.slice(0, 5));
      } else {
        const [statsRes, bookingsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/bookings?limit=5`),
        ]);
        setStats(statsRes.data);
        setRecentBookings(bookingsRes.data.items || []);
      }
    } catch (error) {
      toast.error(t('dashboard_page.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role) fetchData(user.role);
  }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const isCustomer = user?.role === 'CUSTOMER';

  const _localeMap = { en: 'en-GB', es: 'es-ES', fi: 'fi-FI', sv: 'sv-SE', de: 'de-DE' };
  const _loc = _localeMap[lang] || 'de-DE';

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(_loc, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (iso) => new Date(iso).toLocaleDateString(_loc);

  const getStatusBadge = (status) => {
    const styles = {
      PENDING: 'badge-warning',
      CONFIRMED: 'badge-success',
      CANCELLED: 'badge-error'
    };
    const labelKey = {
      PENDING:   'bookings.status_pending',
      CONFIRMED: 'bookings.status_confirmed',
      CANCELLED: 'bookings.status_cancelled',
      COMPLETED: 'bookings.status_completed',
    }[status];
    return <span className={styles[status] || 'badge-info'}>{labelKey ? t(labelKey) : status}</span>;
  };

  const getPaymentStatusBadge = (status) => {
    const styles = {
      DEPOSIT_DUE: 'badge-warning',
      DEPOSIT_PAID: 'badge-info',
      BALANCE_DUE: 'badge-warning',
      BALANCE_PAID: 'badge-success',
      REFUNDED: 'badge-error'
    };
    const labelKey = {
      DEPOSIT_DUE:  'dashboard_page.payment_deposit_due',
      DEPOSIT_PAID: 'dashboard_page.payment_deposit_paid',
      BALANCE_DUE:  'dashboard_page.payment_balance_due',
      BALANCE_PAID: 'dashboard_page.payment_balance_paid',
      REFUNDED:     'dashboard_page.payment_refunded',
    }[status];
    return <span className={styles[status] || 'badge-info'}>{labelKey ? t(labelKey) : status?.replace('_', ' ')}</span>;
  };

  if (!user?.role || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const isAdminOrCS = ['ADMIN', 'CUSTOMER_SERVICE'].includes(user?.role);

  // ── Customer-specific dashboard ────────────────────────────────────────────
  if (isCustomer) {
    const { totalBookings, upcomingBookings, loyaltyPoints } = customerStats;
    // Points to next milestone (every 100 points)
    const nextMilestone = Math.ceil((loyaltyPoints + 1) / 100) * 100;
    const progressPct = loyaltyPoints === 0 ? 0 : Math.min(100, Math.round((loyaltyPoints % 100) / 100 * 100));

    return (
      <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0c2431] font-['Outfit']">
              {t('dashboard.welcome', { name: user?.name?.split(' ')[0] || '' })}
            </h1>
            <p className="text-[#194a50]/70 mt-1">{t('dashboard_page.customer_subtitle')}</p>
          </div>
          <Link to="/browse">
            <Button className="bg-[#194a50] hover:bg-[#10555e] rounded-full shrink-0">
              <Search className="w-4 h-4 mr-2" />
              {t('dashboard_page.book_a_stay')}
            </Button>
          </Link>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="stats-grid">
          <StatsCard title={t('dashboard_page.my_bookings')} value={totalBookings} icon={CalendarDays} color="primary" />
          <StatsCard title={t('dashboard_page.upcoming_stays')} value={upcomingBookings} icon={CheckCircle2} color="success" />
          <StatsCard title={t('dashboard_page.loyalty_points')} value={loyaltyPoints.toLocaleString(_loc)} icon={Star} color="gold" />
        </div>

        {/* Loyalty card */}
        <div
          className="relative overflow-hidden rounded-2xl shadow-md"
          style={{ background: 'linear-gradient(135deg, #0c2431 0%, #194a50 55%, #1e6068 100%)' }}
          data-testid="loyalty-card"
        >
          {/* decorative circles */}
          <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full opacity-10" style={{ background: '#d8a28c' }} />
          <div className="absolute -bottom-16 -left-8 w-48 h-48 rounded-full opacity-10" style={{ background: '#d8a28c' }} />

          <div className="relative px-6 py-7 sm:px-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
              {/* Left: points info */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#d8a28c' }}>
                    <Star className="w-4 h-4 text-white fill-white" />
                  </div>
                  <span className="text-sm font-semibold tracking-wide text-white/70 uppercase">{t('dashboard_page.oceanhome_rewards')}</span>
                </div>
                <p className="text-5xl font-bold text-white font-['Outfit'] leading-none">
                  {loyaltyPoints.toLocaleString(_loc)}
                </p>
                <p className="text-white/60 text-sm mt-1">{t('dashboard_page.points_balance')}</p>

                {/* Progress to next milestone */}
                <div className="mt-4 space-y-1.5">
                  <div className="flex justify-between text-xs text-white/50">
                    <span>{t('dashboard_page.pts_to_milestone', { current: loyaltyPoints % 100 })}</span>
                    <span>{t('dashboard_page.pts_value', { n: nextMilestone })}</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${progressPct}%`, background: '#d8a28c' }}
                    />
                  </div>
                </div>
              </div>

              {/* Right: coming soon */}
              <div className="flex flex-col gap-3 sm:items-end">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#d8a28c]/40 text-[#d8a28c] text-xs font-medium">
                  <Gift className="w-3.5 h-3.5" />
                  {t('dashboard_page.redeem_soon')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Bookings */}
        <Card className="shadow-sm border-[#194a50]/5" data-testid="recent-bookings-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-semibold text-[#0c2431] font-['Outfit']">
              {t('dashboard_page.my_recent_bookings')}
            </CardTitle>
            <Link to="/bookings" className="text-sm text-[#194a50] hover:text-[#10555e] flex items-center gap-1">
              {t('dashboard.view_all')} <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {recentBookings.length === 0 ? (
              <div className="text-center py-10 text-[#194a50]/60">
                <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="font-medium">{t('dashboard_page.no_bookings')}</p>
                <p className="text-sm mt-1 opacity-70">{t('dashboard_page.no_bookings_hint')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('bookings.reference')}</th>
                      <th>{t('bookings.property')}</th>
                      <th>{t('dashboard_page.dates')}</th>
                      <th>{t('common.status')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentBookings.map((booking) => (
                      <tr key={booking.id} data-testid={`booking-row-${booking.reference}`}>
                        <td className="font-medium text-[#0c2431]">{booking.reference}</td>
                        <td className="text-[#194a50]">{booking.property_title || 'N/A'}</td>
                        <td className="text-sm text-[#194a50]/70">
                          {formatDate(booking.start_date)} – {formatDate(booking.end_date)}
                        </td>
                        <td>{getStatusBadge(booking.status)}</td>
                        <td>
                          <Link to={`/bookings/${booking.id}`} className="text-[#194a50] hover:text-[#10555e]">
                            <ArrowRight className="w-4 h-4" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }
  // ── End customer dashboard ─────────────────────────────────────────────────

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#0c2431] font-['Outfit']">
            {t('dashboard.welcome', { name: user?.name?.split(' ')[0] || '' })}
          </h1>
          <p className="text-[#194a50]/70 mt-1">{t('dashboard_page.staff_subtitle')}</p>
        </div>
        {isAdminOrCS && (
          <Link to="/bookings">
            <Button className="bg-[#194a50] hover:bg-[#10555e] rounded-full" data-testid="new-booking-btn">
              <CalendarDays className="w-4 h-4 mr-2" />
              {t('dashboard.new_booking')}
            </Button>
          </Link>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="stats-grid">
        {user?.role === 'OWNER' ? (
          <>
            <StatsCard
              title={t('dashboard_page.my_properties')}
              value={stats?.properties || 0}
              icon={Building2}
              color="primary"
            />
            <StatsCard
              title={t('dashboard_page.total_bookings')}
              value={stats?.total_bookings || 0}
              icon={CalendarDays}
              color="secondary"
            />
            <StatsCard
              title={t('dashboard_page.active_bookings')}
              value={stats?.active_bookings || 0}
              icon={CheckCircle2}
              color="success"
            />
          </>
        ) : (
          <>
            <StatsCard
              title={t('dashboard_page.total_properties')}
              value={stats?.total_properties || 0}
              icon={Building2}
              color="primary"
            />
            <StatsCard
              title={t('dashboard_page.total_bookings')}
              value={stats?.total_bookings || 0}
              icon={CalendarDays}
              color="secondary"
            />
            <StatsCard
              title={t('dashboard_page.pending')}
              value={stats?.pending_bookings || 0}
              icon={Clock}
              color="warning"
            />
            <StatsCard
              title={t('dashboard_page.total_revenue')}
              value={formatCurrency(stats?.total_revenue)}
              icon={DollarSign}
              color="success"
              isText
            />
          </>
        )}
      </div>

      {/* Recent Bookings */}
      <Card className="shadow-sm border-[#194a50]/5" data-testid="recent-bookings-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl font-semibold text-[#0c2431] font-['Outfit']">
            {t('dashboard.recent_bookings')}
          </CardTitle>
          <Link to="/bookings" className="text-sm text-[#194a50] hover:text-[#10555e] flex items-center gap-1">
            {t('dashboard.view_all')} <ArrowRight className="w-4 h-4" />
          </Link>
        </CardHeader>
        <CardContent>
          {recentBookings.length === 0 ? (
            <div className="text-center py-8 text-[#194a50]/60">
              <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t('dashboard_page.no_bookings')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('bookings.reference')}</th>
                    <th>{t('bookings.property')}</th>
                    <th>{t('dashboard_page.dates')}</th>
                    <th>{t('common.status')}</th>
                    {user?.role !== 'OWNER' && <th>{t('dashboard_page.payment')}</th>}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {recentBookings.map((booking) => (
                    <tr key={booking.id} data-testid={`booking-row-${booking.reference}`}>
                      <td className="font-medium text-[#0c2431]">{booking.reference}</td>
                      <td className="text-[#194a50]">{booking.property_title || 'N/A'}</td>
                      <td className="text-sm text-[#194a50]/70">
                        {formatDate(booking.start_date)} - {formatDate(booking.end_date)}
                      </td>
                      <td>{getStatusBadge(booking.status)}</td>
                      {user?.role !== 'OWNER' && <td>{getPaymentStatusBadge(booking.payment_status)}</td>}
                      <td>
                        <Link
                          to={`/bookings/${booking.id}`}
                          className="text-[#194a50] hover:text-[#10555e]"
                        >
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats for Admin */}
      {user?.role === 'ADMIN' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="shadow-sm border-[#194a50]/5" data-testid="owners-stats-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#0c2431] font-['Outfit']">
                {t('dashboard_page.owners_customers')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-[#194a50]/70">{t('dashboard_page.property_owners')}</p>
                    <p className="text-xl font-bold text-[#0c2431]">{stats?.total_owners || 0}</p>
                  </div>
                </div>
                <Link to="/owners" className="text-sm text-[#194a50] hover:text-[#10555e]">
                  {t('dashboard_page.manage_arrow')}
                </Link>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-[#194a50]/70">{t('dashboard_page.customers')}</p>
                    <p className="text-xl font-bold text-[#0c2431]">{stats?.total_customers || 0}</p>
                  </div>
                </div>
                <Link to="/users" className="text-sm text-[#194a50] hover:text-[#10555e]">
                  {t('dashboard_page.view_all_arrow')}
                </Link>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm border-[#194a50]/5" data-testid="quick-actions-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#0c2431] font-['Outfit']">
                {t('dashboard.quick_actions')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/properties" className="block">
                <Button variant="outline" className="w-full justify-start border-[#194a50]/20 hover:bg-[#194a50]/5">
                  <Building2 className="w-4 h-4 mr-2 text-[#194a50]" />
                  {t('dashboard_page.add_new_property')}
                </Button>
              </Link>
              <Link to="/calendar" className="block">
                <Button variant="outline" className="w-full justify-start border-[#194a50]/20 hover:bg-[#194a50]/5">
                  <CalendarDays className="w-4 h-4 mr-2 text-[#194a50]" />
                  {t('dashboard_page.view_calendar')}
                </Button>
              </Link>
              <Link to="/payouts" className="block">
                <Button variant="outline" className="w-full justify-start border-[#194a50]/20 hover:bg-[#194a50]/5">
                  <DollarSign className="w-4 h-4 mr-2 text-[#194a50]" />
                  {t('dashboard_page.manage_payouts')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const StatsCard = ({ title, value, icon: Icon, color, isText }) => {
  const colorClasses = {
    primary:   'bg-[#194a50] text-white',
    secondary: 'bg-[#d8a28c] text-[#0c2431]',
    success:   'bg-green-500 text-white',
    warning:   'bg-amber-500 text-white',
    gold:      'bg-gradient-to-br from-amber-400 to-amber-500 text-white',
  };

  return (
    <Card className="shadow-sm border-[#194a50]/5 hover:shadow-md transition-shadow" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-[#194a50]/70 mb-1">{title}</p>
            <p className={`font-bold text-[#0c2431] ${isText ? 'text-2xl' : 'text-3xl'}`}>
              {value}
            </p>
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DashboardPage;
